import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { supabase } from '@/lib/supabaseClient';
import { setDemoScene, setDemoAccess } from '@/lib/demo';
import { fetchSceneDefs, setSceneShowLive, adminPathForScene, adminLinkForScene, samePath, PART_ADMIN_LINKS, PART_META, isEyesUpScene } from '@/lib/demoScenes';
import { postStage, isProjectorWindow } from '@/lib/stageChannel';
import { useToast } from '@/components/ui/use-toast';
import useLivePartPrices from '@/hooks/useLivePartPrices';
import { ChevronLeft, ChevronRight, ChevronsDownUp, Mail, MonitorPlay, PoundSterling, Presentation, Unlock } from 'lucide-react';

// ── DemoNavPad ────────────────────────────────────────────────────────────────
// The presenter's floating deck remote. While a demo session is live it stays
// on screen across EVERY admin page — the full console on the Subcontractors
// page is for setup; this pad is for delivering. It carries the scene pills
// and Prev/Next, and advancing a scene does two things at once: every attendee
// device moves with it (via demo-control, as ever), and the ADMIN's own screen
// navigates to the page that proves that scene (PART_ADMIN_LINKS), so the
// projector is always already showing the right surface.
//
// Renders nothing unless a non-ended session exists. Realtime keeps it in step
// with the console and with any other admin; relaxed polling is the safety
// net. Collapses to a small float button so it never buries the page.
const POLL_MS = 12000;

const DemoNavPad = () => {
  useLivePartPrices(); // pricing chips follow the admin's Parts figures
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [session, setSession] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem('demo_nav_pad_open') !== '0'; } catch { return true; }
  });
  const [busy, setBusy] = useState(false);
  const endedRef = useRef(false);

  const setOpenPersist = (v) => {
    setOpen(v);
    try { sessionStorage.setItem('demo_nav_pad_open', v ? '1' : '0'); } catch { /* private mode */ }
  };

  // Live session watcher: realtime first, relaxed poll as the net.
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const { data } = await supabase
          .from('demo_sessions')
          .select('*')
          .neq('status', 'ended')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (stop) return;
        endedRef.current = !data;
        setSession(data || null);
      } catch { /* keep last known state */ }
    };
    const channel = supabase
      .channel('demo-nav-pad')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'demo_sessions' }, load)
      .subscribe();
    load();
    const id = setInterval(load, POLL_MS);
    return () => { stop = true; clearInterval(id); supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    if (!session) return undefined;
    if (scenes.length > 0) return undefined;
    let stale = false;
    fetchSceneDefs().then((list) => { if (!stale) setScenes(list); }).catch(() => {});
    return () => { stale = true; };
  }, [session, scenes.length]);

  const sceneIdx = useMemo(() => {
    if (scenes.length === 0) return 0;
    return Math.max(0, Math.min(scenes.length - 1, session?.current_scene ?? 0));
  }, [scenes, session?.current_scene]);
  const scene = scenes[sceneIdx];
  const part = PART_META[scene?.part_key];
  const showPricing = session?.show_pricing !== false;
  const accessColumns = session ? session.access_mode !== undefined && session.access_mode !== null : false;
  const pricingColumn = session ? session.show_pricing !== undefined : false;

  // The double act: move every attendee to the scene AND move this admin's
  // own screen to the page that proves it. Eyes-up scenes (numbers) have no
  // proving page and leave the admin where they are.
  // Open or shut the big screen for the CURRENT scene, mid-presentation. The
  // console's Big screen row is on the Product Demos page, which the presenter
  // has navigated away from by the time the room asks to see something — so the
  // same control has to live on the pad that follows them.
  const flipLiveScreen = async () => {
    // Silent returns here read as "the button does nothing" (owner, 22 Sep) —
    // every refusal now says why, and success acts INSTANTLY: the presenter
    // is taken to the scene's page and the backdrop/projector are told to
    // re-read scenes instead of waiting out their poll.
    const path = adminPathForScene(scene);
    if (!scene?.id) {
      toast({ title: 'Deck is the built-in fallback', description: 'Author scenes in Product Demos to use the big-screen switch.' });
      return;
    }
    if (!path) {
      toast({ title: 'No admin page for this scene', description: 'The room stays on the curtain here — that is the intended cover.' });
      return;
    }
    const nextOn = !scene.show_live;
    setBusy(true);
    setScenes((list) => list.map((x, n) => (n === sceneIdx ? { ...x, show_live: nextOn } : x)));
    try {
      await setSceneShowLive(scene.id, nextOn);
      postStage({ type: 'scenes-refresh' });
      if (nextOn && !samePath(location.pathname, path)) navigate(path);
      toast({ title: nextOn ? 'Big screen: showing your page' : 'Big screen: back on the curtain', className: nextOn ? 'bg-emerald-600 text-white' : undefined });
    } catch (err) {
      setScenes((list) => list.map((x, n) => (n === sceneIdx ? { ...x, show_live: !nextOn } : x)));
      toast({ title: 'Could not change the big screen', description: err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const goToScene = async (next) => {
    if (!session || scenes.length === 0 || busy) return;
    const clamped = Math.max(0, Math.min(scenes.length - 1, next));
    if (clamped === sceneIdx) return;
    const before = session.current_scene;
    setSession((s) => ({ ...s, current_scene: clamped }));
    // Navigate FIRST — the presenter's screen moves the instant the button is
    // pressed. Awaiting the edge function before navigating made every scene
    // change drag by the function's round trip (owner: "pages switch super
    // slowly", 1 Oct). The room sync runs behind it; a failure reverts the
    // scene and says so. Scene-aware: a sub_route scene lands on ITS admin
    // page (Kit your crew → the Uniforms desk), not the part's default board.
    const link = adminLinkForScene(scenes[clamped]);
    if (link && !samePath(location.pathname, link.path)) navigate(link.path);
    setBusy(true);
    try {
      await setDemoScene(session.id, clamped);
    } catch (e) {
      setSession((s) => ({ ...s, current_scene: before }));
      toast({ title: 'Scene change failed', description: `The room is still on the previous scene — ${e.message}`, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const togglePricing = async () => {
    if (!session || busy) return;
    setBusy(true);
    const next = !showPricing;
    setSession((s) => ({ ...s, show_pricing: next }));
    try { await setDemoAccess(session.id, { showPricing: next }); }
    catch (e) {
      setSession((s) => ({ ...s, show_pricing: !next }));
      toast({ title: 'Pricing switch failed', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  // The Terminations scene's touch of class: a watermarked DEMO letter to
  // every attendee's signed-with email. demo-control refuses non-admins and
  // never touches the real termination flow.
  const sendDemoLetters = async () => {
    if (!session || busy) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('demo-control', {
        body: { action: 'demo_letters', session_id: session.id },
      });
      if (error) throw new Error(error.message || 'send failed');
      if (data?.error) throw new Error(data.error);
      toast({ title: `Demo letters sent to ${data?.sent ?? 0} attendee${(data?.sent ?? 0) === 1 ? '' : 's'}`, description: 'Watermarked demonstration only — nothing changes their access.', className: 'bg-emerald-600 text-white' });
    } catch (e) {
      toast({ title: 'Letters did not send', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const toggleFreeRoam = async () => {
    if (!session || busy) return;
    setBusy(true);
    const next = session.access_mode === 'free' ? 'script' : 'free';
    const before = session.access_mode;
    setSession((s) => ({ ...s, access_mode: next }));
    try { await setDemoAccess(session.id, { accessMode: next }); }
    catch (e) {
      setSession((s) => ({ ...s, access_mode: before }));
      toast({ title: 'Free roam switch failed', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  // Never on the projector window: on a live scene that window renders a real
  // admin page, and the room must not see the presenter's remote (14 Oct).
  if (isProjectorWindow() || !session || scenes.length === 0) return null;

  return (
    <>
      {/* Collapsed: a small float button that says which scene is live */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="pad-pill"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            onClick={() => setOpenPersist(true)}
            className="fixed bottom-5 left-1/2 z-[70] flex -translate-x-1/2 items-center gap-2 rounded-full border border-primary/40 bg-slate-950/95 px-4 py-2.5 text-white shadow-2xl backdrop-blur-md"
            aria-label={`Open the demo nav pad — scene ${sceneIdx + 1} of ${scenes.length}`}
          >
            <Presentation className="h-4 w-4 text-primary" />
            <span className="text-xs font-black uppercase tracking-wider">Demo {sceneIdx + 1}/{scenes.length}</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Expanded: the pad. Fixed bottom-centre, above content, below modals. */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="pad"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-2 bottom-3 z-[70] mx-auto max-w-3xl rounded-2xl border border-primary/30 bg-slate-950/95 p-3 text-white shadow-2xl backdrop-blur-md"
            role="toolbar"
            aria-label="Demo navigation pad"
          >
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary text-[11px] font-black">{sceneIdx + 1}</span>
              <p className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wider">
                {scene?.title}
                {part && (
                  <span className="ml-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[9px] font-bold normal-case tracking-normal text-amber-300">
                    {part.name} · {part.van}{showPricing ? ` · £${part.price}/crew` : ''}
                  </span>
                )}
              </p>
              {/* Eyes-up scenes go live on their own (sceneStage) — the chip
                  only exists for revealing a hands-on scene's office side. */}
              {!!adminPathForScene(scene) && !!scene?.id && !isEyesUpScene(scene) && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={flipLiveScreen}
                  title={scene.show_live
                    ? 'The room is seeing your REAL screen on this scene — tap for the backdrop'
                    : 'The room is seeing the backdrop — tap to show your real screen'}
                  aria-pressed={!!scene.show_live}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-wide ${scene.show_live
                    ? 'border-emerald-400/60 bg-emerald-400/15 text-emerald-300'
                    : 'border-white/20 text-slate-400'}`}
                >
                  <MonitorPlay className="h-3 w-3" /> Screen
                </button>
              )}
              {scene?.part_key === 'terminations' && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={sendDemoLetters}
                  title="Send every attendee a watermarked DEMO termination letter to the email they signed with"
                  className="inline-flex items-center gap-1 rounded-full border border-rose-400/50 bg-rose-400/10 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-rose-300"
                >
                  <Mail className="h-3 w-3" /> Letters
                </button>
              )}
              {pricingColumn && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={togglePricing}
                  title={showPricing ? 'Pricing is SHOWING on attendee screens — tap to hide it' : 'Pricing is HIDDEN on attendee screens — tap to show it'}
                  aria-pressed={showPricing}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-wide ${showPricing
                    ? 'border-amber-400/60 bg-amber-400/15 text-amber-300'
                    : 'border-white/20 text-slate-400 line-through'}`}
                >
                  <PoundSterling className="h-3 w-3" /> Pricing
                </button>
              )}
              {accessColumns && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={toggleFreeRoam}
                  title={session.access_mode === 'free' ? 'Free roam is ON — attendees can open everything' : 'Open everything for Q&A'}
                  aria-pressed={session.access_mode === 'free'}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-wide ${session.access_mode === 'free'
                    ? 'border-emerald-400/60 bg-emerald-400/15 text-emerald-300'
                    : 'border-white/20 text-slate-400'}`}
                >
                  <Unlock className="h-3 w-3" /> Roam
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpenPersist(false)}
                className="rounded-md border border-white/10 p-1 text-slate-400 hover:text-white"
                aria-label="Collapse the demo nav pad"
              >
                <ChevronsDownUp className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="mt-2 flex items-center gap-1.5 overflow-x-auto pb-0.5">
              <button
                type="button"
                disabled={busy || sceneIdx === 0}
                onClick={() => goToScene(sceneIdx - 1)}
                className="shrink-0 rounded-lg border border-white/15 p-1.5 text-slate-300 hover:text-white disabled:opacity-30"
                aria-label="Previous scene"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {scenes.map((mod, i) => {
                const active = i === sceneIdx;
                const eyesUp = isEyesUpScene(mod);
                return (
                  <button
                    key={mod.id || `${mod.part_key}-${i}`}
                    type="button"
                    disabled={busy}
                    onClick={() => goToScene(i)}
                    title={`${mod.title}${eyesUp ? ' (eyes up)' : ''}`}
                    aria-pressed={active}
                    className={`shrink-0 rounded-lg border px-2 py-1 text-[11px] font-bold ${active
                      ? 'border-primary bg-primary text-white'
                      : eyesUp
                        ? 'border-white/10 text-slate-500 hover:text-slate-300'
                        : 'border-white/15 text-slate-300 hover:border-primary/50 hover:text-white'}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
              <button
                type="button"
                disabled={busy || sceneIdx >= scenes.length - 1}
                onClick={() => goToScene(sceneIdx + 1)}
                className="ml-auto flex shrink-0 items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-black uppercase tracking-wider text-white shadow-glow disabled:opacity-40"
                aria-label="Next scene"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DemoNavPad;
